import { useOrganization } from "@clerk/nextjs"

import {
  buildScopedStorageKey,
  type StorageScope,
} from "@/utils/scopedStorageKey"

import { useOptionalWorkspaceContext } from "../contexts/WorkspaceContext"

/**
 * Scope a `localStorage` base key to the active organization (and, by
 * default, the active workspace once it resolves). Returns `undefined` only
 * until the organization itself has resolved — callers pass that straight
 * through to `useFilterParams`'s `storageKey`, `useLocalStorage`, or a raw
 * `localStorage` call, which treat `undefined` as "don't persist yet". A
 * `"workspace"`-scoped key degrades to organization scope rather than
 * `undefined` when no workspace id is resolved (see `buildScopedStorageKey`).
 *
 * Default to `scope: "workspace"` for anything read on a workspace-scoped
 * route (conversations, runs, dashboard, ...). Use `scope: "organization"`
 * only for state that belongs to an organization-global route (e.g.
 * Settings) — see `ORGANIZATION_GLOBAL_ROUTE_ROOTS` in `workspaceRoutes.ts`.
 */
export function useScopedStorageKey(
  base: string,
  scope: StorageScope = "workspace",
): string | undefined {
  const { organization } = useOrganization()
  const workspaceContext = useOptionalWorkspaceContext()

  return buildScopedStorageKey(base, scope, {
    organizationId: organization?.id,
    workspaceId: workspaceContext?.currentWorkspace?.id,
  })
}
